import { Spin } from "antd";
import { ColDef } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import moment from "moment";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef
} from "react";
import { AUDIT_PROJECT_USER_EVENTS } from "admin-app/services/ciq-queries";
import { useCIQQuery } from "hooks/ciq-gql-hooks";
import AuditTimeline, {
  type TAuditTimelineItem
} from "../components/audit-timeline";
import type {
  TProjectUserRow,
  TProjectUserMasterRow
} from "../hooks/entity-audit-data";

function formatPersonName(
  user: {
    first_name: string | null;
    last_name: string | null;
  } | null
): string {
  if (!user) return "";
  return `${user.first_name || ""} ${user.last_name || ""}`.trim();
}

type TProjectUserAuditEvent = {
  event_log_id: string;
  field: string | null;
  new_bool: boolean | null;
  new_string: string | null;
  old_bool: boolean | null;
  old_string: string | null;
  created_by: string | null;
  created_at: string | null;
  created_by_user: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
  } | null;
  event_log: {
    source: string | null;
    event: {
      name: string | null;
      description: string | null;
    } | null;
  } | null;
};

function formatActorDisplayName(eventRecord: TProjectUserAuditEvent): string {
  const user = eventRecord.created_by_user;
  const fullName = `${user?.first_name || ""} ${user?.last_name || ""}`.trim();
  if (fullName) {
    return user?.email ? `${fullName} (${user.email})` : fullName;
  }
  if (user?.email) {
    return user.email;
  }
  return eventRecord.created_by || "Unknown";
}

function buildProjectUserTimelineItems(
  records: TProjectUserAuditEvent[]
): TAuditTimelineItem[] {
  const groupedRecords: Record<string, TProjectUserAuditEvent[]> = {};

  for (let index = 0; index < records.length; index += 1) {
    const record = records[index];
    const groupId = record.event_log_id || `event-${index}`;
    if (!groupedRecords[groupId]) {
      groupedRecords[groupId] = [];
    }
    groupedRecords[groupId].push(record);
  }

  return Object.entries(groupedRecords)
    .map(([groupId, groupItems]) => {
      const firstItem = groupItems[0];
      return {
        id: groupId,
        timestamp: firstItem?.created_at || null,
        label:
          firstItem?.event_log?.event?.description ||
          firstItem?.event_log?.event?.name ||
          "Project user event",
        description: `By: ${formatActorDisplayName(firstItem)}${
          firstItem?.event_log?.source
            ? ` | Source: ${firstItem.event_log.source}`
            : ""
        }`,
        changes: groupItems.map((eventItem) => {
          const oldValue =
            eventItem.old_string ??
            (eventItem.old_bool !== null ? `${eventItem.old_bool}` : null);
          const newValue =
            eventItem.new_string ??
            (eventItem.new_bool !== null ? `${eventItem.new_bool}` : null);
          return {
            field: eventItem.field || "field",
            fromValue: oldValue,
            toValue: newValue
          };
        })
      };
    })
    .sort((firstItem, secondItem) => {
      const firstTime = firstItem.timestamp
        ? moment(firstItem.timestamp).valueOf()
        : 0;
      const secondTime = secondItem.timestamp
        ? moment(secondItem.timestamp).valueOf()
        : 0;
      return secondTime - firstTime;
    });
}

function ProjectUserTimelineDetailRenderer(props: {
  data?: TProjectUserMasterRow;
}) {
  const projectId = props?.data?.projectUser?.project_id;
  const userId = props?.data?.projectUser?.user_id;
  const { data, loading } = useCIQQuery<{
    audit_project_events: TProjectUserAuditEvent[];
  }>(AUDIT_PROJECT_USER_EVENTS, {
    variables: {
      project_id: projectId,
      new_user_id: userId
    },
    skip: !projectId || !userId
  });

  const timelineItems = useMemo(
    () => buildProjectUserTimelineItems(data?.audit_project_events || []),
    [data?.audit_project_events]
  );

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-md p-4 my-2 mx-3 min-h-[120px] flex items-center justify-center">
        <Spin size="small" />
      </div>
    );
  }

  return (
    <AuditTimeline
      items={timelineItems}
      emptyText="No project user audit events available."
    />
  );
}

export type TProjectUsersAuditGridProps = {
  loading: boolean;
  filteredRows: TProjectUserRow[];
};

export type TProjectUsersAuditGridHandle = {
  setQuickFilterText: (text: string) => void;
};

const ProjectUsersAuditGrid = forwardRef<
  TProjectUsersAuditGridHandle,
  TProjectUsersAuditGridProps
>(function ProjectUsersAuditGrid(props, ref) {
  const { loading, filteredRows } = props;
  const gridRef = useRef<AgGridReact<TProjectUserMasterRow>>(null);

  useImperativeHandle(ref, () => ({
    setQuickFilterText: (text: string) => {
      gridRef.current?.api?.setGridOption("quickFilterText", text);
    }
  }));

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      resizable: true,
      filter: true,
      flex: 1,
      minWidth: 160
    }),
    []
  );

  const columnDefs = useMemo<ColDef<TProjectUserMasterRow>[]>(() => {
    return [
      {
        colId: "groupTitle",
        field: "groupTitle",
        headerName: "Name",
        cellRenderer: "agGroupCellRenderer",
        flex: 1,
        minWidth: 200
      },
      {
        colId: "email",
        field: "email",
        headerName: "Email",
        flex: 1,
        minWidth: 200
      },
      {
        colId: "roleName",
        field: "roleName",
        headerName: "Role",
        flex: 0,
        width: 160,
        minWidth: 140
      },
      {
        colId: "statusName",
        field: "statusName",
        headerName: "Status",
        flex: 0,
        width: 140,
        minWidth: 120
      },
      {
        colId: "external",
        field: "external",
        headerName: "External",
        flex: 0,
        width: 100,
        minWidth: 90,
        valueGetter: ({ data }) => (data?.external ? "Yes" : "No")
      },
      {
        colId: "createdBy",
        field: "createdBy",
        headerName: "Created By",
        flex: 0,
        width: 160,
        minWidth: 140
      },
      {
        colId: "createdAt",
        field: "createdAt",
        headerName: "Created At",
        valueGetter: ({ data }) =>
          data?.createdAt ? moment(data.createdAt).format("MM-DD-YYYY") : "",
        flex: 0,
        width: 130,
        minWidth: 120
      },
      {
        colId: "updatedBy",
        field: "updatedBy",
        headerName: "Updated By",
        flex: 0,
        width: 160,
        minWidth: 140
      },
      {
        colId: "updatedAt",
        field: "updatedAt",
        headerName: "Updated At",
        sort: "asc",
        valueGetter: ({ data }) =>
          data?.updatedAt ? moment(data.updatedAt).format("MM-DD-YYYY") : "",
        flex: 0,
        width: 130,
        minWidth: 120
      }
    ];
  }, []);

  useEffect(() => {
    gridRef.current?.api?.setGridOption("loading", loading);
  }, [loading]);

  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api) {
      return;
    }

    api.applyColumnState({
      state: [{ colId: "updatedAt", sort: "asc" }],
      defaultState: { sort: null }
    });
  }, []);

  const rowData = useMemo<TProjectUserMasterRow[]>(() => {
    return filteredRows.map((row) => {
      const displayName =
        formatPersonName(row.user).trim() ||
        row.user?.email?.trim() ||
        row.user_id;
      return {
        groupKey: row.id,
        groupTitle: displayName,
        email: row.user?.email || "",
        roleName: row.project_role?.name || "",
        statusName: row.status?.name || "",
        external: row.external,
        createdBy: formatPersonName(row.created_by_user),
        createdAt: row.created_at,
        updatedBy: formatPersonName(row.updated_by_user),
        updatedAt: row.updated_at,
        projectUser: row
      };
    });
  }, [filteredRows]);

  return (
    <AgGridReact<TProjectUserMasterRow>
      ref={gridRef}
      rowData={rowData}
      columnDefs={columnDefs}
      defaultColDef={defaultColDef}
      suppressContextMenu
      masterDetail
      isRowMaster={() => true}
      detailCellRenderer={ProjectUserTimelineDetailRenderer}
      detailRowHeight={380}
      getRowId={(params) => params.data.groupKey}
      animateRows
    />
  );
});

export default ProjectUsersAuditGrid;
